import {
  Dropdown,
  DropdownButton,
  DropdownItem,
  DropdownMenu,
} from "@/components/dropdown";
import { DropdownLogOut } from "@/components/DropdownLogOut";
import {
  Sidebar,
  SidebarBody,
  SidebarFooter,
  SidebarHeader,
  SidebarItem,
  SidebarLabel,
  SidebarSection,
  SidebarSpacer,
} from "@/components/sidebar";
import { ChevronUpIcon } from "@heroicons/react/16/solid";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import clsx from "clsx";

type Tool = {
  name: string;
  href: string;
  hidden: boolean;
};

interface MiraSidebarProps {
  session: {
    user: {
      account: number;
      entity: string;
      firstName: string;
      lastName: string;
      fullName: string;
      role: string;
      state: number;
    };
  } | null;
  modules?: {
    absence_overview?: boolean;
    absence_requests?: boolean;
  };
}

export function MiraSidebar({ session, modules }: MiraSidebarProps) {
  const location = useLocation();
  const { logout } = useAuth();
  
  const isManager = session?.user?.role === 'manager' || session?.user?.role === 'auditor';
  const isAuditor = session?.user?.role === 'auditor';
  
  // Simplified enterprise navigation - only essential items
  // Items are conditionally shown based on module availability and user role
  const tools: Tool[] = [
    {
      name: "Dashboard",
      href: "/",
      hidden: false,
    },
    {
      name: "Abwesenheiten",
      href: "/absence",
      hidden: !modules?.absence_requests, // Hidden if module not enabled
    },
    {
      name: "Team Übersicht",
      href: "/team",
      hidden: !modules?.absence_overview, // Hidden if module not enabled
    },
    {
      name: "Anträge verwalten",
      href: "/manager/absences",
      hidden: !isManager, // Only visible for managers/auditors
    },
    {
      name: "Mitarbeiter",
      href: "/auditor/employees",
      hidden: !isAuditor, // Only visible for auditors
    },
  ];

  return (
    <Sidebar>
      <SidebarHeader>
        <SidebarSection className="max-lg:hidden">
          {/* Logo only - clean and professional */}
          <div className="px-2 py-3">
            <img src="/avemio-logo.png" alt="Avemio" className="h-12 w-auto" />
          </div>
        </SidebarSection>
      </SidebarHeader>
      <SidebarBody>
        {/* Primary Navigation */}
        <SidebarSection>
          {tools
            .filter(({ hidden }) => !hidden) // Filter out hidden items
            .map(({ href, name }) => {
              // Check if this is the current page (also match sub-routes for detail pages)
              const isCurrent = location.pathname === href || 
                (href !== '/' && location.pathname.startsWith(href));
              
              return (
                <SidebarItem
                  href={href}
                  key={href}
                  current={isCurrent}
                >
                  <SidebarLabel className="text-sm font-medium">{name}</SidebarLabel>
                </SidebarItem>
              );
            })}
        </SidebarSection>
        
        <SidebarSpacer />
      </SidebarBody>
      <SidebarFooter className="max-lg:hidden">
        {/* Enterprise user section with entity context */}
        <Dropdown>
          <DropdownButton as={SidebarItem}>
            <div className="flex min-w-0 items-center w-full">
              <div className="flex-1 min-w-0 space-y-0.5">
                {/* Full Name */}
                <div className="text-sm font-semibold text-gray-900 dark:text-avemio-dark-text-primary truncate leading-tight">
                  {session?.user?.fullName || 'User'}
                </div>
                {/* Entity Name with @ prefix for context */}
                <div className="text-xs text-gray-600 dark:text-avemio-dark-text-secondary truncate leading-tight">
                  @ {session?.user?.entity || 'Entity'}
                </div>
              </div>
              <ChevronUpIcon className="size-4 text-gray-600 dark:text-avemio-dark-text-secondary ml-2" />
            </div>
          </DropdownButton>
          <DropdownMenu className="min-w-56" anchor="top start">
            <DropdownItem onClick={logout}>
              <DropdownLogOut />
            </DropdownItem>
          </DropdownMenu>
        </Dropdown>
      </SidebarFooter>
    </Sidebar>
  );
}
